import { useState } from 'react'
import { Segmented } from '../components'
import { useActiveOrg } from '../app-state/active-org'
import { useOrganizations } from '../lib/api/organizations'
import { useForecast } from '../lib/api/forecast'
import { WeekRow } from './forecast/week-row'
import { BreakdownDrawer } from './forecast/breakdown-drawer'

type Props = {
  railSelected: string
}

export function Forecast({ railSelected: _railSelected }: Props) {
  const [weeks, setWeeks] = useState<4 | 8 | 12>(8)
  const [openWeek, setOpenWeek] = useState<{ weekStart: string; weekId: string } | null>(null)

  // Resolve org: prefer activeOrgId from rail, fall back to first org.
  const { activeOrgId } = useActiveOrg()
  const orgs = useOrganizations()
  const selectedOrgId: string | null = activeOrgId ?? orgs.data?.[0]?.id ?? null

  const { data, isLoading } = useForecast({
    organizationId: selectedOrgId ?? '',
    weeks,
    enabled: !!selectedOrgId,
  })

  return (
    <div className="content">
      {/* Week-count toggle */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16,
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <span style={{ fontSize: 13, color: 'var(--fg-3)' }}>
          {data
            ? `Поріг алерту: ${new Intl.NumberFormat('uk-UA', { maximumFractionDigits: 0 }).format(data.threshold)} UAH`
            : ' '}
        </span>
        <Segmented<'4' | '8' | '12'>
          options={[
            { value: '4', label: '4 тижнів' },
            { value: '8', label: '8 тижнів' },
            { value: '12', label: '12 тижнів' },
          ]}
          value={String(weeks) as '4' | '8' | '12'}
          onChange={(v) => setWeeks(Number(v) as 4 | 8 | 12)}
        />
      </div>

      <div className="card">
        <div className="card-head">
          <h3>Прогноз за тижнями</h3>
          {data && (
            <span style={{ color: 'var(--fg-3)', fontSize: 11 }}>
              {data.weeks[0]?.weekStart} → {data.weeks[data.weeks.length - 1]?.weekEnd}
            </span>
          )}
          <div style={{ flex: 1 }} />
          {data && data.weeks.filter((w) => w.flags.belowThreshold).length > 0 && (
            <span className="chip warning">
              {data.weeks.filter((w) => w.flags.belowThreshold).length} тижні з розривом
            </span>
          )}
        </div>

        {!selectedOrgId ? (
          <p style={{ padding: '24px 16px', color: 'var(--fg-3)', fontSize: 13 }}>
            Оберіть організацію для перегляду прогнозу.
          </p>
        ) : isLoading ? (
          <p style={{ padding: '24px 16px', color: 'var(--fg-3)', fontSize: 13 }}>
            Завантаження…
          </p>
        ) : !data || data.weeks.length === 0 ? (
          <p style={{ padding: '24px 16px', color: 'var(--fg-3)', fontSize: 13 }}>
            Немає даних для прогнозу.
          </p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ width: 80 }}>Тиждень</th>
                  <th style={{ width: 180 }}>Період</th>
                  <th className="num" style={{ width: 140 }}>Початок</th>
                  <th className="num" style={{ width: 140 }}>Доходи</th>
                  <th className="num" style={{ width: 140 }}>Витрати</th>
                  <th className="num" style={{ width: 140 }}>Кінець</th>
                  <th style={{ width: 36 }}></th>
                </tr>
              </thead>
              <tbody>
                {data.weeks.map((w) => (
                  <WeekRow
                    key={w.weekId}
                    week={w}
                    onOpen={() => setOpenWeek({ weekStart: w.weekStart, weekId: w.weekId })}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {openWeek && selectedOrgId && (
        <BreakdownDrawer
          organizationId={selectedOrgId}
          week={openWeek.weekId}
          onClose={() => setOpenWeek(null)}
        />
      )}
    </div>
  )
}
